import { CartLinesUpdateEvent } from '@shopify/events';

const root = document.documentElement;
const motion = () => root.classList.contains('at-motion');
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const once = new WeakSet();
const first = (el) => {
  if (once.has(el)) return false;
  once.add(el);
  return true;
};

/* Gold light: a small fragment shader. No library, paused offscreen, skipped on weak devices. */
const FRAG = `precision mediump float;uniform vec2 r;uniform float t;uniform vec2 m;
void main(){vec2 u=gl_FragCoord.xy/r;vec2 q=u*vec2(r.x/r.y,1.)*2.2;float a=0.;
for(int i=0;i<4;i++){q+=vec2(sin(q.y*1.3+t*.13),cos(q.x*1.1-t*.1))*.55;a+=sin(q.x+q.y*.6+t*.07);}
a=abs(sin(a*.9));float b=pow(1.-a,6.);float g=smoothstep(1.15,0.,distance(u,vec2(.5+m.x*.12,.3+m.y*.08)));
vec3 c=mix(vec3(.063,.149,.106),vec3(.165,.353,.247),g*.8);
gl_FragColor=vec4(c+vec3(.91,.76,.42)*b*(.1+.26*g),1.);}`;

function light(canvas) {
  const conn = navigator.connection;
  if (!motion() || conn?.saveData || (navigator.hardwareConcurrency || 8) < 4) return;
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
  if (!gl) return;
  const sh = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return s;
  };
  const p = gl.createProgram();
  gl.attachShader(p, sh(gl.VERTEX_SHADER, 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}'));
  gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return;
  gl.useProgram(p);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(p, 'a');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const U = { r: gl.getUniformLocation(p, 'r'), t: gl.getUniformLocation(p, 't'), m: gl.getUniformLocation(p, 'm') };
  const size = () => {
    const s = 0.6;
    canvas.width = Math.max(1, canvas.clientWidth * s);
    canvas.height = Math.max(1, canvas.clientHeight * s);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(U.r, canvas.width, canvas.height);
  };
  size();
  new ResizeObserver(size).observe(canvas);
  let live = false;
  let raf = 0;
  const pointer = canvas.__pointer || { x: 0, y: 0 };
  canvas.__pointer = pointer;
  const draw = (ms) => {
    gl.uniform1f(U.t, ms / 1000 + 40);
    gl.uniform2f(U.m, pointer.x, pointer.y);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    raf = live ? requestAnimationFrame(draw) : 0;
  };
  const set = (on) => {
    live = on && !document.hidden;
    if (live && !raf) raf = requestAnimationFrame(draw);
  };
  new IntersectionObserver(([e]) => set(e.isIntersecting)).observe(canvas);
  document.addEventListener('visibilitychange', () => set(!document.hidden));
  canvas.classList.add('is-on');
}

function hero(el) {
  if (!first(el)) return;
  const canvas = el.querySelector('[data-at-gl]');
  const stage = el.querySelector('[data-at-stage]');
  const start = () => canvas && light(canvas);
  if (document.readyState === 'complete') requestIdleCallback?.(start) ?? setTimeout(start, 200);
  else addEventListener('load', () => setTimeout(start, 300), { once: true });
  if (!motion() || !stage || !matchMedia('(hover:hover) and (pointer:fine)').matches) return;
  const want = { x: 0, y: 0 };
  const now = { x: 0, y: 0 };
  let raf = 0;
  const tick = () => {
    now.x += (want.x - now.x) * 0.07;
    now.y += (want.y - now.y) * 0.07;
    stage.style.setProperty('--mx', now.x.toFixed(4));
    stage.style.setProperty('--my', now.y.toFixed(4));
    if (canvas) canvas.__pointer = Object.assign(canvas.__pointer || {}, now);
    raf = Math.abs(want.x - now.x) + Math.abs(want.y - now.y) > 0.001 ? requestAnimationFrame(tick) : 0;
  };
  el.addEventListener('pointermove', (e) => {
    const r = el.getBoundingClientRect();
    want.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    want.y = ((e.clientY - r.top) / r.height) * 2 - 1;
    if (!raf) raf = requestAnimationFrame(tick);
  });
  el.addEventListener('pointerleave', () => {
    want.x = want.y = 0;
    if (!raf) raf = requestAnimationFrame(tick);
  });
}

/* Scroll engine: one passive listener, work only for what is on screen. */
const scrollers = new Set();
let queued = false;
const run = () => {
  queued = false;
  scrollers.forEach((f) => f());
};
const queue = () => {
  if (!queued) {
    queued = true;
    requestAnimationFrame(run);
  }
};
addEventListener('scroll', queue, { passive: true, capture: true });
addEventListener('resize', queue, { passive: true });

function watch(el, fn) {
  new IntersectionObserver(([e]) => {
    if (e.isIntersecting) {
      scrollers.add(fn);
      queue();
    } else scrollers.delete(fn);
  }).observe(el);
}

function reveal(el) {
  if (!first(el) || !motion()) return;
  const chapters = [...el.querySelectorAll('.at-reveal__chapter')];
  const plate = el.querySelector('.at-reveal__plate');
  const wide = matchMedia('(min-width: 990px)');
  el.style.setProperty('--n', chapters.length);
  el.classList.add('is-live');
  chapters[0]?.classList.add('is-on');
  watch(el, () => {
    if (!wide.matches) return;
    const r = el.getBoundingClientRect();
    const span = r.height - innerHeight;
    const p = clamp(-r.top / span);
    plate.style.setProperty('--m', clamp(p * 3).toFixed(4));
    plate.style.setProperty('--sheen', (clamp(p * 1.4) * 2 - 1).toFixed(3));
    const idx = Math.min(chapters.length - 1, Math.floor(p * chapters.length));
    chapters.forEach((c, i) => c.classList.toggle('is-on', i === idx));
  });
}

function parallax(el) {
  if (!first(el) || !motion()) return;
  watch(el, () => {
    const r = el.getBoundingClientRect();
    el.style.setProperty('--py', clamp((r.top + r.height / 2) / innerHeight, -0.2, 1.2) * 2 - 1);
  });
}

const seen = new IntersectionObserver(
  (items) =>
    items.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        seen.unobserve(e.target);
      }
    }),
  { rootMargin: '0px 0px -8% 0px' }
);

function orbit(el) {
  if (!first(el)) return;
  const stage = el.querySelector('.at-orbit__stage');
  const ring = el.querySelector('.at-orbit__ring');
  const items = [...ring.children];
  const detail = el.querySelector('.at-orbit__detail');
  const n = items.length;
  if (n < 3) return;
  const step = 360 / n;
  let turn = 0;
  let front = -1;
  items.forEach((it, i) => it.style.setProperty('--i', i));
  const fit = () => {
    const w = stage.clientWidth;
    const card = Math.min(w * 0.56, 300);
    const radius = (card / 2 + 14) / Math.tan(Math.PI / n);
    stage.style.setProperty('--card', `${card}px`);
    stage.style.setProperty('--radius', `${Math.round(radius)}px`);
    stage.style.setProperty('--step', `${step}deg`);
  };
  const show = () => {
    ring.style.setProperty('--turn', `${turn}deg`);
    const idx = ((Math.round(-turn / step) % n) + n) % n;
    if (idx === front) return;
    front = idx;
    items.forEach((it, i) => {
      it.classList.toggle('is-front', i === idx);
      it.querySelector('a').tabIndex = i === idx ? 0 : -1;
    });
    detail.replaceChildren(items[idx].querySelector('.at-orbit__info').cloneNode(true));
  };
  const go = (d) => {
    turn = (Math.round(turn / step) - d) * step;
    show();
  };
  el.classList.add('is-3d');
  fit();
  show();
  new ResizeObserver(fit).observe(stage);
  el.querySelector('[data-at-prev]').addEventListener('click', () => go(-1));
  el.querySelector('[data-at-next]').addEventListener('click', () => go(1));
  el.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') go(-1);
    if (e.key === 'ArrowRight') go(1);
  });
  let start = null;
  let moved = 0;
  stage.addEventListener('pointerdown', (e) => {
    start = { x: e.clientX, t: turn };
    moved = 0;
  });
  addEventListener('pointermove', (e) => {
    if (!start) return;
    const dx = e.clientX - start.x;
    moved = Math.abs(dx);
    if (moved > 6) stage.classList.add('is-drag');
    turn = start.t + dx * 0.28;
    ring.style.setProperty('--turn', `${turn}deg`);
  });
  const end = () => {
    if (!start) return;
    start = null;
    stage.classList.remove('is-drag');
    turn = Math.round(turn / step) * step;
    show();
  };
  addEventListener('pointerup', end);
  addEventListener('pointercancel', end);
  stage.addEventListener('click', (e) => moved > 6 && e.preventDefault(), true);
}

/* Real cart: same request and event Horizon's product form uses, so the drawer and count update. */
document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-at-buy]') || typeof Theme === 'undefined') return;
  e.preventDefault();
  const btn = form.querySelector('[type=submit]');
  const msg = form.querySelector('.at-buy__msg');
  const fd = new FormData(form);
  const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  if (ids.length) fd.append('sections', ids.join(','));
  const deferred = CartLinesUpdateEvent.createPromise();
  btn.disabled = true;
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(fd.get('id')), quantity: 1 }],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: fd,
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    }).then((r) => r.json());
    if (res.status) throw new Error(res.description || res.message);
    const cart = await fetch('/cart.js').then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'atelier',
        sourceId: form.dataset.formId || 'atelier',
        itemCount: 1,
        productId: form.dataset.productId,
        sections: res.sections,
        didError: false,
      },
    });
    if (msg) msg.textContent = form.dataset.added || '';
  } catch (err) {
    deferred.reject(err);
    if (msg) msg.textContent = err.message;
  } finally {
    btn.disabled = false;
  }
});

function init() {
  document.querySelectorAll('[data-at-hero]').forEach(hero);
  document.querySelectorAll('[data-at-reveal]').forEach(reveal);
  document.querySelectorAll('[data-at-parallax]').forEach(parallax);
  document.querySelectorAll('[data-at-orbit]').forEach(orbit);
  document.querySelectorAll('[data-at-in]').forEach((n) => seen.observe(n));
  root.classList.add('at-ready');
}
init();
document.addEventListener('shopify:section:load', init);
